import {
  IMPORT_FILE_EXTENSIONS,
  MAX_IMPORT_FILE_BYTES,
  type ImportDuplicate,
  type ImportExcludedRow,
  type ImportField,
  type ImportOverrides,
  type ImportPreview,
  type ImportRow,
} from '@/lib/api/requirement-spreadsheets';
import type { LibraryMetadata } from '@/lib/api/requirement-library';
import { matchKindWords } from '@/components/requirement-library/library-logic';

/**
 * Pure logic of the spreadsheet import review (T-0153). No React, no
 * requests: what a file may be, how a reviewer's change becomes the
 * `overrides` object, how the server's preview becomes table rows and
 * counts, and the sentences shown for the server's reason codes.
 */

export function plural(n: number, noun: string, pluralNoun = `${noun}s`): string {
  return `${n.toLocaleString()} ${n === 1 ? noun : pluralNoun}`;
}

// ---------------------------------------------------------------------------
// Files
// ---------------------------------------------------------------------------

export const IMPORT_ACCEPT = IMPORT_FILE_EXTENSIONS.join(',');

/**
 * Why a file cannot be sent, or null when it can. A first check only: the
 * server reads the file and its answer is the one that counts.
 */
export function fileProblem(file: { name: string; size: number }): string | null {
  const lower = file.name.toLowerCase();
  if (!IMPORT_FILE_EXTENSIONS.some((ext) => lower.endsWith(ext))) {
    return 'Not a spreadsheet. Use an .xlsx, .xls, .xlsm or .csv file.';
  }
  if (file.size === 0) return 'The file is empty.';
  if (file.size > MAX_IMPORT_FILE_BYTES) return 'The file is larger than 20 MB.';
  return null;
}

/** "XLSX", "CSV": the badge on the file header. */
export function fileBadge(name: string): string {
  const m = /\.([A-Za-z0-9]+)$/.exec(name.trim());
  return (m?.[1] ?? 'file').toUpperCase().slice(0, 4);
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ---------------------------------------------------------------------------
// Overrides
// ---------------------------------------------------------------------------

export function withSheetOverride(
  overrides: ImportOverrides,
  sheet: string,
  change: { use?: boolean; header_row?: number },
): ImportOverrides {
  return {
    ...overrides,
    sheets: { ...overrides.sheets, [sheet]: { ...overrides.sheets?.[sheet], ...change } },
  };
}

export function withColumnMapping(
  overrides: ImportOverrides,
  header: string,
  field: ImportField | null,
): ImportOverrides {
  return { ...overrides, column_mapping: { ...overrides.column_mapping, [header]: field } };
}

export function withCriticality(
  overrides: ImportOverrides,
  label: string,
  value: string,
): ImportOverrides {
  return { ...overrides, criticality_map: { ...overrides.criticality_map, [label]: value } };
}

/** `moduleId` null leaves the rows of that module name out. */
export function withModule(
  overrides: ImportOverrides,
  name: string,
  moduleId: string | null,
): ImportOverrides {
  return { ...overrides, module_map: { ...overrides.module_map, [name]: moduleId } };
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

export const FIELD_LABEL: Record<ImportField, string> = {
  client_ref: 'Client reference',
  module: 'Module',
  subcategory: 'Sub-category',
  text: 'Requirement text',
  criticality: 'Criticality',
};

export const IMPORT_CRITICALITY_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'critical', label: 'Critical' },
  { value: 'desired', label: 'Desired' },
  { value: 'not_required', label: 'Not required' },
  { value: 'not_applicable', label: 'Not applicable' },
  { value: 'not_set', label: 'Not set' },
];

export function excludedReason(row: ImportExcludedRow): string {
  switch (row.reason) {
    case 'no_text':
      return 'No requirement text';
    case 'text_too_long':
      return 'Text is over 2,000 characters';
    case 'subcategory_too_long':
      return 'Sub-category is over 120 characters';
    case 'repeated_header':
      return 'Repeats the header row';
    case 'duplicate_in_file':
      return row.duplicate_of
        ? `Same text as row ${row.duplicate_of.row} of ${row.duplicate_of.sheet}`
        : 'Same text as an earlier row of this file';
    case 'module_skipped':
      return 'Its module is left out';
    default:
      return 'Cannot be imported';
  }
}

export function refusalReason(row: ImportRow): string {
  const existing = row.refusal?.existing;
  if (row.refusal?.reason === 'exact_duplicate_in_module') {
    return existing?.number
      ? `Already in this module as ${existing.number}`
      : 'Already in this module';
  }
  if (row.refusal?.reason === 'duplicate_in_file') {
    return existing?.row != null
      ? `Same text as row ${existing.row} of this file`
      : 'Same text as an earlier row of this file';
  }
  return 'Cannot be added';
}

/** What a duplicate points at: "UB-C01", or "row 5 of this file". */
export function duplicateTarget(d: ImportDuplicate): string {
  if (d.kind === 'in_file') {
    return `row ${d.row ?? '?'} of this file${d.client_ref ? ` (${d.client_ref})` : ''}`;
  }
  return d.number ?? 'an existing requirement';
}

export function duplicateLabel(d: ImportDuplicate): string {
  return `${d.exact ? 'Same as' : 'Similar to'} ${duplicateTarget(d)}`;
}

/**
 * Why the row was flagged. The library intake says which kind of match it
 * is (T-0154): "same wording", "similar wording" with the word overlap, or
 * "similar meaning" (different words, so there is no overlap to quote).
 * Without a kind: "same text", or the word overlap as a percentage.
 */
export function duplicateScore(d: ImportDuplicate): string {
  const overlap = `${Math.round(d.score * 100)}% of the words match`;
  const words = matchKindWords(d.match_kind);
  if (words) return d.match_kind === 'wording' ? `${words}, ${overlap}` : words;
  return d.exact ? 'same text' : overlap;
}

/** The sentence under the footer counts for what still blocks the confirm. */
export function blockerMessage(preview: ImportPreview): string | null {
  const b = preview.blockers;
  if (b.includes('module_retired')) return 'This module is retired. Restore it to add to it.';
  if (b.includes('no_text_column')) return 'Map a column to Requirement text to continue.';
  if (b.includes('unknown_modules')) {
    return `Choose a library module for ${plural(preview.unknown_modules.length, 'module name')} to continue.`;
  }
  if (b.includes('unknown_criticality_labels')) {
    return `Choose a criticality for ${plural(preview.unknown_criticality_labels.length, 'label')} to continue.`;
  }
  if (b.includes('nothing_to_import')) return 'Nothing in this file can be imported.';
  return null;
}

// ---------------------------------------------------------------------------
// Rows and counts
// ---------------------------------------------------------------------------

export type DisplayKind = 'new' | 'duplicate' | 'needs_mapping' | 'refused' | 'excluded';

export interface DisplayRow {
  ref: string;
  sheet: string;
  row: number;
  clientRef: string | null;
  /** Module the row will be filed under; the file's own name while unmapped. */
  module: string | null;
  subcategory: string | null;
  text: string;
  /** Library only: labelled metadata split off the end of the row's text. */
  metadata: LibraryMetadata | null;
  criticality: string | null;
  criticalityLabel: string | null;
  kind: DisplayKind;
  duplicates: ReadonlyArray<ImportDuplicate>;
  /** Why an excluded or refused row is left out. */
  reason: string | null;
}

function refOrder(ref: string): [number, number] {
  const [sheet = '0', row = '0'] = ref.split(':');
  return [Number(sheet) || 0, Number(row) || 0];
}

/** Every row of the used sheets, in file order: importable, flagged and left out. */
export function displayRows(preview: ImportPreview): DisplayRow[] {
  const out: DisplayRow[] = preview.rows.map((r) => ({
    ref: r.ref,
    sheet: r.sheet,
    row: r.row,
    clientRef: r.client_ref,
    module: r.module_name ?? r.source_module_name ?? null,
    subcategory: r.subcategory,
    text: r.text,
    metadata: r.metadata ?? null,
    criticality: r.criticality ?? null,
    criticalityLabel: r.criticality_label ?? null,
    kind:
      r.status === 'duplicate' || r.status === 'needs_mapping' || r.status === 'refused'
        ? r.status
        : 'new',
    duplicates: r.duplicates,
    reason: r.status === 'refused' ? refusalReason(r) : null,
  }));
  for (const e of preview.excluded) {
    out.push({
      ref: e.ref,
      sheet: e.sheet,
      row: e.row,
      clientRef: e.client_ref,
      module: null,
      subcategory: null,
      text: e.preview ?? '',
      metadata: null,
      criticality: null,
      criticalityLabel: null,
      kind: 'excluded',
      duplicates: [],
      reason: excludedReason(e),
    });
  }
  return out.sort((a, b) => {
    const [as, ar] = refOrder(a.ref);
    const [bs, br] = refOrder(b.ref);
    return as - bs || ar - br;
  });
}

export type ImportTab = 'all' | 'duplicates' | 'excluded' | 'refused';

export function rowsForTab(rows: ReadonlyArray<DisplayRow>, tab: ImportTab): DisplayRow[] {
  if (tab === 'all') return rows.slice();
  const kind: DisplayKind =
    tab === 'duplicates' ? 'duplicate' : tab === 'refused' ? 'refused' : 'excluded';
  return rows.filter((r) => r.kind === kind);
}

/** Refs of the rows the server currently flags as likely duplicates. */
export function duplicateRefs(preview: ImportPreview): string[] {
  return preview.rows.filter((r) => r.status === 'duplicate').map((r) => r.ref);
}

/** Kept refs that are still flagged rows of the latest preview. */
export function keptRefs(preview: ImportPreview, keep: ReadonlySet<string>): string[] {
  return duplicateRefs(preview).filter((ref) => keep.has(ref));
}

export interface ImportTotals {
  /** New rows plus the flagged rows the reviewer keeps. */
  toImport: number;
  duplicates: number;
  duplicatesKept: number;
  duplicatesSkipped: number;
  excluded: number;
  refused: number;
  needsMapping: number;
}

/** The server's latest counts, with the reviewer's keep/skip choices applied. */
export function importTotals(preview: ImportPreview, keep: ReadonlySet<string>): ImportTotals {
  const kept = keptRefs(preview, keep).length;
  const c = preview.counts;
  return {
    toImport: c.to_import + kept,
    duplicates: c.likely_duplicates,
    duplicatesKept: kept,
    duplicatesSkipped: c.likely_duplicates - kept,
    excluded: c.excluded,
    refused: c.refused ?? 0,
    needsMapping: c.needs_mapping ?? 0,
  };
}

/** True when the preview can be confirmed as it stands. */
export function canConfirmImport(
  preview: ImportPreview,
  keep: ReadonlySet<string>,
  acknowledgedRepeat: boolean,
): boolean {
  if (!preview.can_confirm) return false;
  if (preview.repeat_of && !acknowledgedRepeat) return false;
  return importTotals(preview, keep).toImport > 0;
}
